import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, User, MoreHorizontal, MessageCircle,
  ShoppingCart, Package, Wallet, Inbox, ShieldCheck, BarChart3, X, Truck, ClipboardList, FolderOpen, Factory, Sparkles, LifeBuoy, Boxes, Recycle,
} from "lucide-react";

const iconClass = "h-5 w-5";

/** Sticky bottom navigation bar — visible on mobile only (md:hidden). */
export default function BottomNav() {
  const { user, hasModule } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [invLow, setInvLow] = useState(0);

  // Poll the chat unread total every 25 seconds so the Chat tab surfaces a
  // red pill without keeping a socket open on background pages. Cheap: the
  // backend does a couple of indexed count queries.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const pull = () =>
      api.get("/chat/unread-count")
        .then(({ data }) => { if (!cancelled) setChatUnread(data.count || 0); })
        .catch(() => {});
    pull();
    const id = setInterval(pull, 25000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  // Poll the low-stock count every 45s so the Inventory tab lights up when
  // any product falls at/below its min. Only fires when the user actually
  // has the products module — non-biz roles never see the tab anyway.
  const canSeeInv = !!user && hasModule("products");
  useEffect(() => {
    if (!canSeeInv) return;
    let cancelled = false;
    const pull = () =>
      api.get("/inventory/low-stock-count")
        .then(({ data }) => { if (!cancelled) setInvLow(data.count || 0); })
        .catch(() => {});
    pull();
    const id = setInterval(pull, 45000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canSeeInv]);

  if (!user) return null;

  const isBiz = !!user;   // module gate does fine-grained control now
  const isAdmin = user.role === "admin" || user.role === "manager";
  const anyMfg = ["mfg.dashboard","mfg.machines","mfg.moulds","mfg.products","mfg.materials","mfg.shifts","mfg.production","mfg.hourly_log","mfg.reports","mfg.payroll"].some(hasModule);
  const anyCrm = ["crm.dashboard","crm.leads","crm.pipeline","crm.activities"].some(hasModule);

  const tabs = [
    { to: "/dashboard",  label: "Tasks",   icon: LayoutDashboard, testid: "botnav-tasks" },
    { to: "/chat",       label: "Chat",    icon: MessageCircle,   testid: "botnav-chat" },
    // Inventory tab — one-tap stock ledger for operators. Same gate as
    // AppLayout desktop nav (``products`` module) so it stays hidden for
    // non-business roles.
    isBiz && hasModule("products") && { to: "/inventory", label: "Inventory", icon: Boxes, testid: "botnav-inventory" },
    // Big red Help tab — always one tap away for operators.
    { to: "/team-guide", label: "Help",    icon: LifeBuoy,        testid: "botnav-help", highlight: true },
    { to: "/profile",    label: "Profile", icon: User,            testid: "botnav-profile" },
  ].filter(Boolean);

  const moreItems = [
    { to: "/team", label: "Team", icon: Users },
    isBiz  && hasModule("orders")          && { to: "/orders",           label: "Orders",     icon: ShoppingCart },
    isAdmin && hasModule("shop_requests")  && { to: "/order-requests",   label: "Requests",   icon: Inbox },
    isBiz  && hasModule("customers")       && { to: "/customers",        label: "Customers",  icon: Users },
    isBiz  && hasModule("products")        && { to: "/products",         label: "Products",   icon: Package },
    isBiz  && hasModule("products")        && { to: "/material-logs",    label: "Material Production", icon: Recycle },
    isBiz  && hasModule("suppliers")       && { to: "/suppliers",        label: "Suppliers",  icon: Truck },
    isBiz  && hasModule("purchase_orders") && { to: "/purchase-orders",  label: "POs",        icon: ClipboardList },
    anyMfg                        && { to: "/mfg",              label: "Manufacturing", icon: Factory },
    anyCrm                        && { to: "/crm",              label: "CRM",        icon: Sparkles },
    isBiz  && hasModule("expenses")        && { to: "/expenses",         label: "Expenses",   icon: Wallet },
    hasModule("files")                     && { to: "/files",       label: "Files",   icon: FolderOpen },
    { to: "/reports",     label: "Reports", icon: BarChart3 },
    isAdmin && { to: "/admin",         label: "Admin",      icon: ShieldCheck },
  ].filter(Boolean);

  const showMoreTab = moreItems.length > 0;

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-neutral-950 border-t border-black dark:border-neutral-800 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length + (showMoreTab ? 1 : 0)}, minmax(0, 1fr))` }}>
          {tabs.map(({ to, label, icon: Icon, testid, highlight }) => {
            const active = to === "/dashboard"
              ? location.pathname === "/" || location.pathname.startsWith("/dashboard")
              : location.pathname.startsWith(to);
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-bold uppercase tracking-wider ${
                  highlight
                    ? active
                      ? "text-white bg-[#DC262F]"
                      : "text-[#DC262F]"
                    : active
                      ? "text-[#FF3B30]"
                      : "text-neutral-500 dark:text-neutral-400"
                }`}
              >
                <div className="relative">
                  <Icon className={iconClass} strokeWidth={active ? 2.5 : 2} />
                  {to === "/chat" && chatUnread > 0 && (
                    <span
                      data-testid="botnav-chat-badge"
                      className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-black flex items-center justify-center shadow-md">
                      {chatUnread > 99 ? "99+" : chatUnread}
                    </span>
                  )}
                  {to === "/inventory" && invLow > 0 && (
                    <span
                      data-testid="botnav-inventory-badge"
                      className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC262F] text-white text-[10px] font-black flex items-center justify-center shadow-md ring-2 ring-white dark:ring-neutral-950">
                      {invLow > 99 ? "99+" : invLow}
                    </span>
                  )}
                </div>
                <span>{label}</span>
                {active && <span className="absolute top-0 h-[3px] w-8 bg-[#FF3B30]" />}
              </NavLink>
            );
          })}
          {showMoreTab && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              data-testid="botnav-more"
              className="flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
            >
              <MoreHorizontal className={iconClass} />
              <span>More</span>
            </button>
          )}
        </div>
      </nav>

      {/* Bottom-sheet for "More" */}
      {moreOpen && (
        <div
          data-testid="botnav-more-sheet"
          className="md:hidden fixed inset-0 z-50 bg-black/40"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute bottom-0 inset-x-0 bg-white dark:bg-neutral-950 border-t-2 border-black dark:border-neutral-700 rounded-t-2xl p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 dark:text-neutral-400">/ menu</div>
              <button onClick={() => setMoreOpen(false)} data-testid="botnav-more-close" className="p-1.5 -m-1.5">
                <X className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {moreItems.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  data-testid={`botnav-more-${label.toLowerCase()}`}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 border border-neutral-200 dark:border-neutral-800 hover:border-[#FF3B30] active:bg-[#FF3B30]/10"
                >
                  <Icon className="h-6 w-6 text-[#0D0D0D] dark:text-white" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
